import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import {
  OPTIONS,
  formatMoney,
} from "../lib/finance";
import OpenAccountBlock from "../components/OpenAccountBlock";
import { Sparkles, ArrowRight, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedView() {
  const { token } = useParams();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/shared/${token}`);
        if (!cancel) setData(res.data);
      } catch (err) {
        if (!cancel) setError(err?.response?.data?.detail || t("shared_expired"));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [token, t]);

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center text-lg text-[#4A4A4A]">
        …
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16" data-testid="shared-error">
        <h1 className="font-serif text-3xl font-semibold text-[#101A42]">
          {t("shared_expired")}
        </h1>
        <Button
          asChild
          className="mt-8 min-h-[56px] rounded-full bg-[#A762B1] px-6 text-lg font-semibold text-white hover:bg-[#B775C0]"
        >
          <Link to="/">{t("start_now")}</Link>
        </Button>
      </div>
    );
  }

  const reco = data.recommendation;
  const opt = OPTIONS[reco.recommendation_id];
  const localized = opt?.[lang] || opt?.fr || {
    name: reco.recommendation_id,
    short: "",
    why: "",
    example: "",
  };
  const currency = "EUR"; // shared view uses EUR by default for display simplicity

  return (
    <div className="mx-auto max-w-3xl px-6 py-12" data-testid="shared-view">
      <p className="text-base font-semibold uppercase tracking-widest text-[#A762B1]">
        {t("shared_from")} {data.sender_name} — {t("shared_wants_to_share")}
      </p>
      {data.message && (
        <blockquote
          data-testid="shared-message"
          className="mt-6 rounded-2xl border-2 border-[#101A42]/15 bg-[#F8EFD9] p-5 font-serif text-xl italic leading-relaxed text-[#101A42]"
        >
          « {data.message} »
        </blockquote>
      )}

      <div
        data-testid="shared-reco-card"
        className="mt-8 rounded-2xl border-2 border-[#101A42] bg-gradient-to-b from-[#F8EFD9] to-white p-6 sm:p-8"
      >
        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-[#101A42]/10 px-3 py-1.5 text-sm font-semibold text-[#101A42]">
          <Sparkles className="h-4 w-4" />
          {lang === "fr" ? "Recommandation Sereno" : "Sereno recommendation"}
        </div>
        <h1 className="font-serif text-3xl font-semibold text-[#101A42] sm:text-4xl">
          {localized.name}
        </h1>
        <p className="mt-2 text-lg text-[#4A4A4A]">{localized.short}</p>

        <div className="mt-6 rounded-xl bg-white p-5 border border-[#E8DFC7]">
          <p className="text-base font-semibold uppercase tracking-wider text-[#101A42]">
            {t("why_this")}
          </p>
          <p className="mt-2 text-lg leading-relaxed text-[#101A42]">
            {localized.why}
          </p>
        </div>

        <div className="mt-4 rounded-xl bg-[#101A42] p-5 text-white">
          <p className="flex items-center gap-2 text-base font-semibold uppercase tracking-wider text-[#F8EFD9]">
            <BookOpen className="h-4 w-4" />
            {t("concrete_example")}
          </p>
          <p className="mt-2 text-lg leading-relaxed">{localized.example}</p>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-[#E8DFC7] bg-white p-4">
            <p className="text-sm text-[#4A4A4A]">
              {lang === "fr" ? "Chaque mois" : "Each month"}
            </p>
            <p className="mt-1 font-serif text-2xl font-semibold text-[#101A42]">
              {formatMoney(reco.monthly_amount, currency, lang)}
            </p>
          </div>
          <div className="rounded-xl border border-[#E8DFC7] bg-white p-4">
            <p className="text-sm text-[#4A4A4A]">
              {lang === "fr" ? "Durée" : "Duration"}
            </p>
            <p className="mt-1 font-serif text-2xl font-semibold text-[#101A42]">
              {reco.horizon_years} {t("years")}
            </p>
          </div>
          <div className="rounded-xl border-2 border-[#A762B1] bg-[#A762B1]/5 p-4">
            <p className="text-sm text-[#4A4A4A]">
              {t(`in_${reco.horizon_years}_years`) || t("projected")}
            </p>
            <p className="mt-1 font-serif text-2xl font-semibold text-[#101A42]">
              {formatMoney(reco.projected_value, currency, lang)}
            </p>
          </div>
        </div>
      </div>

      <OpenAccountBlock optionId={reco.recommendation_id} />

      <div className="mt-10 text-center">
        <Button
          asChild
          data-testid="shared-join-btn"
          className="min-h-[64px] rounded-full bg-[#A762B1] px-8 text-xl font-semibold text-white hover:bg-[#B775C0]"
        >
          <Link to="/decouvrir">
            {t("shared_join")}
            <ArrowRight className="ml-2 h-6 w-6" />
          </Link>
        </Button>
      </div>
    </div>
  );
}
